/**
 * LadekostenzuschussAdmin — CRUD fuer § 3 Nr. 46 EStG Pauschale (Issue #200)
 */

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useLadekostenzuschuss } from '@/hooks/useLadekostenzuschuss';
import {
  standardPauschale,
  PAUSCHALE_MIT_LADUNG_EUR,
  PAUSCHALE_OHNE_LADUNG_EUR,
} from '@/lib/ladekostenzuschuss';
import type { Ladekostenzuschuss } from '@/lib/database.types';
import { Zap, Plus, Edit2, PauseCircle, Trash2, Loader2, X, Save } from 'lucide-react';

const KOOP_RED = '#C41E3A';

interface MitarbeiterOption {
  id: string;
  name: string;
}

interface FahrzeugOption {
  id: string;
  label: string;
}

interface FormState {
  mitarbeiter_id: string;
  fahrzeug_id: string;
  hat_arbeitsplatz_ladung: boolean;
  pauschale_eur: string;
  gueltig_ab: string;
  gueltig_bis: string;
  pauschale_manuell_geaendert: boolean;
}

const leerState = (): FormState => ({
  mitarbeiter_id: '',
  fahrzeug_id: '',
  hat_arbeitsplatz_ladung: false,
  pauschale_eur: String(PAUSCHALE_OHNE_LADUNG_EUR),
  gueltig_ab: new Date().toISOString().slice(0, 10),
  gueltig_bis: '',
  pauschale_manuell_geaendert: false,
});

export default function LadekostenzuschussAdmin() {
  const {
    eintraege,
    loading,
    erstelleEintrag,
    aktualisiereEintrag,
    deaktiviereEintrag,
    loescheEintrag,
  } = useLadekostenzuschuss();

  const [mitarbeiter, setMitarbeiter] = useState<MitarbeiterOption[]>([]);
  const [fahrzeuge, setFahrzeuge] = useState<FahrzeugOption[]>([]);
  const [formOffen, setFormOffen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(leerState());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [{ data: maData }, { data: fzData }] = await Promise.all([
        supabase.from('mitarbeiter').select('id, vorname, nachname').order('nachname'),
        supabase.from('fahrzeuge').select('id, marke, modell, kennzeichen').order('kennzeichen'),
      ]);
      setMitarbeiter(
        (maData || []).map((m: any) => ({ id: m.id, name: `${m.vorname} ${m.nachname}` })),
      );
      setFahrzeuge(
        (fzData || []).map((f: any) => ({
          id: f.id,
          label: `${f.kennzeichen || '—'} · ${f.marke} ${f.modell}`.trim(),
        })),
      );
    })();
  }, []);

  const mitarbeiterMap = useMemo(() => {
    const m = new Map<string, string>();
    mitarbeiter.forEach(x => m.set(x.id, x.name));
    return m;
  }, [mitarbeiter]);

  const fahrzeugMap = useMemo(() => {
    const m = new Map<string, string>();
    fahrzeuge.forEach(x => m.set(x.id, x.label));
    return m;
  }, [fahrzeuge]);

  const handleLadungChange = (hat: boolean) => {
    setForm(s => ({
      ...s,
      hat_arbeitsplatz_ladung: hat,
      pauschale_eur: s.pauschale_manuell_geaendert ? s.pauschale_eur : String(standardPauschale(hat)),
    }));
  };

  const handlePauschaleChange = (wert: string) => {
    setForm(s => ({ ...s, pauschale_eur: wert, pauschale_manuell_geaendert: true }));
  };

  const oeffneNeu = () => {
    setEditId(null);
    setForm(leerState());
    setFormOffen(true);
  };

  const oeffneBearbeiten = (e: Ladekostenzuschuss) => {
    setEditId(e.id);
    setForm({
      mitarbeiter_id: e.mitarbeiter_id,
      fahrzeug_id: e.fahrzeug_id,
      hat_arbeitsplatz_ladung: e.hat_arbeitsplatz_ladung,
      pauschale_eur: String(e.pauschale_eur),
      gueltig_ab: e.gueltig_ab,
      gueltig_bis: e.gueltig_bis ?? '',
      pauschale_manuell_geaendert: e.pauschale_eur !== standardPauschale(e.hat_arbeitsplatz_ladung),
    });
    setFormOffen(true);
  };

  const schliesseForm = () => {
    setFormOffen(false);
    setEditId(null);
    setForm(leerState());
  };

  const handleSpeichern = async () => {
    setSaving(true);
    const eingabe = {
      mitarbeiter_id: form.mitarbeiter_id,
      fahrzeug_id: form.fahrzeug_id,
      hat_arbeitsplatz_ladung: form.hat_arbeitsplatz_ladung,
      pauschale_eur: parseFloat(form.pauschale_eur) || 0,
      gueltig_ab: form.gueltig_ab,
      gueltig_bis: form.gueltig_bis || null,
    };
    const ergebnis = editId
      ? await aktualisiereEintrag(editId, eingabe)
      : await erstelleEintrag(eingabe);
    setSaving(false);
    if (ergebnis.ok) {
      toast.success(editId ? 'Eintrag aktualisiert' : 'Eintrag angelegt');
      schliesseForm();
    } else {
      toast.error(ergebnis.error || 'Speichern fehlgeschlagen');
    }
  };

  const handleDeaktivieren = async (id: string) => {
    const ergebnis = await deaktiviereEintrag(id);
    if (ergebnis.ok) {
      toast.success('Eintrag deaktiviert (gueltig_bis = heute)');
    } else {
      toast.error(ergebnis.error || 'Deaktivierung fehlgeschlagen');
    }
  };

  const handleLoeschen = async (id: string) => {
    if (!window.confirm('Eintrag wirklich loeschen?')) return;
    const ergebnis = await loescheEintrag(id);
    if (ergebnis.ok) {
      toast.success('Eintrag geloescht');
    } else {
      toast.error(ergebnis.error || 'Loeschen fehlgeschlagen');
    }
  };

  const istAktiv = (e: Ladekostenzuschuss): boolean => {
    const heute = new Date().toISOString().slice(0, 10);
    if (e.gueltig_ab > heute) return false;
    if (e.gueltig_bis && e.gueltig_bis < heute) return false;
    return true;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: 'rgba(196,30,58,0.06)' }}
          >
            <Zap className="w-4 h-4" style={{ color: KOOP_RED }} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Ladekostenzuschuss</h3>
            <p className="text-[10px] text-gray-400">§ 3 Nr. 46 EStG — Pauschale je Mitarbeiter / Fahrzeug</p>
          </div>
        </div>

        <button
          onClick={oeffneNeu}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-xs font-bold transition-opacity hover:opacity-90"
          style={{ backgroundColor: KOOP_RED }}
        >
          <Plus className="w-3.5 h-3.5" />
          Neuer Eintrag
        </button>
      </div>

      {/* Formular */}
      {formOffen && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-bold text-gray-900">
              {editId ? 'Eintrag bearbeiten' : 'Neuen Eintrag anlegen'}
            </h4>
            <button
              onClick={schliesseForm}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Schliessen"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-medium text-gray-500 block mb-1">Mitarbeiter</label>
              <select
                value={form.mitarbeiter_id}
                onChange={e => setForm(s => ({ ...s, mitarbeiter_id: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20"
              >
                <option value="">— waehlen —</option>
                {mitarbeiter.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-medium text-gray-500 block mb-1">Fahrzeug</label>
              <select
                value={form.fahrzeug_id}
                onChange={e => setForm(s => ({ ...s, fahrzeug_id: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20"
              >
                <option value="">— waehlen —</option>
                {fahrzeuge.map(f => (
                  <option key={f.id} value={f.id}>{f.label}</option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-2">
              <label className="text-[11px] font-medium text-gray-500 block mb-1">
                Arbeitsplatz-Ladung vorhanden?
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleLadungChange(false)}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${
                    !form.hat_arbeitsplatz_ladung
                      ? 'text-white shadow-sm'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                  style={!form.hat_arbeitsplatz_ladung ? { backgroundColor: KOOP_RED } : undefined}
                >
                  Ohne ({PAUSCHALE_OHNE_LADUNG_EUR} EUR)
                </button>
                <button
                  type="button"
                  onClick={() => handleLadungChange(true)}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${
                    form.hat_arbeitsplatz_ladung
                      ? 'text-white shadow-sm'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                  style={form.hat_arbeitsplatz_ladung ? { backgroundColor: KOOP_RED } : undefined}
                >
                  Mit ({PAUSCHALE_MIT_LADUNG_EUR} EUR)
                </button>
              </div>
            </div>

            <div>
              <label className="text-[11px] font-medium text-gray-500 block mb-1">
                Pauschale (EUR/Monat)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.pauschale_eur}
                onChange={e => handlePauschaleChange(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20"
              />
              <p className="text-[10px] text-gray-400 mt-1">Standard per Toggle, manuell ueberschreibbar.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-medium text-gray-500 block mb-1">Gueltig ab</label>
                <input
                  type="date"
                  value={form.gueltig_ab}
                  onChange={e => setForm(s => ({ ...s, gueltig_ab: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20"
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-gray-500 block mb-1">
                  Gueltig bis <span className="text-gray-300">(leer = unbefristet)</span>
                </label>
                <input
                  type="date"
                  value={form.gueltig_bis}
                  onChange={e => setForm(s => ({ ...s, gueltig_bis: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20"
                />
              </div>
            </div>
          </div>

          <button
            onClick={handleSpeichern}
            disabled={saving}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg text-white text-sm font-bold transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: KOOP_RED }}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {editId ? 'Aenderungen speichern' : 'Eintrag speichern'}
          </button>
        </div>
      )}

      {/* Liste */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100">
          <h4 className="text-sm font-bold text-gray-900">Erfasste Eintraege</h4>
        </div>

        {loading ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
          </div>
        ) : eintraege.length === 0 ? (
          <p className="py-8 text-xs text-gray-400 text-center">Keine Eintraege vorhanden</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Mitarbeiter</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Fahrzeug</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Arbeitsplatz-Ladung</th>
                <th className="px-4 py-2.5 text-right text-[10px] font-bold text-gray-500 uppercase tracking-wider">Pauschale</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Gueltigkeit</th>
                <th className="px-4 py-2.5 text-right text-[10px] font-bold text-gray-500 uppercase tracking-wider">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {eintraege.map(e => {
                const aktiv = istAktiv(e);
                return (
                  <tr key={e.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="px-4 py-2.5 text-xs text-gray-800">
                      {mitarbeiterMap.get(e.mitarbeiter_id) || e.mitarbeiter_id.slice(0, 8)}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-600">
                      {fahrzeugMap.get(e.fahrzeug_id) || e.fahrzeug_id.slice(0, 8)}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-600">
                      {e.hat_arbeitsplatz_ladung ? 'mit' : 'ohne'}
                    </td>
                    <td className="px-4 py-2.5 text-xs font-medium text-right" style={{ color: KOOP_RED }}>
                      {Number(e.pauschale_eur).toFixed(2)} EUR
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-600">
                      <span className={aktiv ? 'text-emerald-600 font-medium' : 'text-gray-400'}>
                        {new Date(e.gueltig_ab).toLocaleDateString('de-DE')}
                        {' – '}
                        {e.gueltig_bis ? new Date(e.gueltig_bis).toLocaleDateString('de-DE') : 'unbefristet'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => oeffneBearbeiten(e)}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"
                          aria-label="Bearbeiten"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        {aktiv && (
                          <button
                            onClick={() => handleDeaktivieren(e.id)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-amber-600"
                            aria-label="Deaktivieren"
                            title="Deaktivieren (gueltig_bis = heute)"
                          >
                            <PauseCircle className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleLoeschen(e.id)}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-red-600"
                          aria-label="Loeschen"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
